import React, { useEffect, useState } from "react";
import { Bell, Zap, Mail, ShieldCheck } from "lucide-react";
import { isPushSupported, getPushPermission } from "../lib/push";
import { api } from "../lib/api";

/**
 * NotificationInlineCard
 * Persuasive block placed on the Home page between the Hero and the inventory.
 * Its job is to convert casual visitors into subscribers with clear value prop,
 * social proof and a single primary CTA that opens the 2-step opt-in prompt.
 *
 * Hides itself if the user is already subscribed to push OR has permanently
 * denied notifications OR the browser doesn't support push.
 */
export const NotificationInlineCard = () => {
  const [hidden, setHidden] = useState(false);
  const [stats, setStats] = useState({ subscribers: 0, cars_new_last_month: 0 });

  useEffect(() => {
    if (!isPushSupported()) { setHidden(true); return; }
    if (getPushPermission() === "denied") { setHidden(true); return; }
    // Consider it visible by default; only hide if we can confirm an active subscription.
    if ("serviceWorker" in navigator && navigator.serviceWorker.getRegistration) {
      navigator.serviceWorker.getRegistration()
        .then((reg) => reg && reg.pushManager && reg.pushManager.getSubscription())
        .then((sub) => { if (sub) setHidden(true); })
        .catch(() => {});
    }
    (async () => {
      try {
        const r = await api.get("/subscribers-stats");
        setStats(r.data || {});
      } catch {}
    })();
  }, []);

  const openPrompt = () => {
    window.dispatchEvent(new CustomEvent("open-push-optin", { detail: { source: "inline" } }));
  };

  if (hidden) return null;

  return (
    <section
      className="relative bg-transparent border-y border-gold/25"
      data-testid="notification-inline-card"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-14 md:py-16">
        <div className="grid md:grid-cols-[auto,1fr,auto] gap-6 md:gap-10 items-center">
          <div className="flex md:justify-center">
            <div className="relative h-16 w-16 md:h-20 md:w-20 rounded-full bg-gold flex items-center justify-center shadow-lg">
              <Bell size={30} className="text-midnight" strokeWidth={2.5} />
              <span className="absolute inset-0 rounded-full ring-2 ring-gold/50 animate-ping" />
            </div>
          </div>
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-3">
              <Zap size={11} /> Le auto migliori vanno via in 24–48h
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-white leading-tight mb-3">
              Sii il <span className="italic text-gold">primo</span> ad essere avvisato
            </h2>
            <p className="text-white/75 text-sm md:text-base leading-relaxed max-w-xl mb-4">
              Attiva le notifiche e ricevi un avviso istantaneo sul telefono quando arriva una nuova auto o parte una
              <span className="text-gold font-semibold"> promozione riservata</span>. Lasciando l'email
              riceverai anche i dettagli completi via posta.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center md:justify-start text-[11px] text-white/60 mb-1">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-gold" /> Nessuno spam</span>
              <span className="inline-flex items-center gap-1.5"><Mail size={12} className="text-gold" /> Disiscriviti in 1 click</span>
              {stats.subscribers >= 5 && (
                <span className="inline-flex items-center gap-1.5 text-emerald-400" data-testid="notification-inline-social-proof">
                  <Bell size={12} /> {stats.subscribers}+ appassionati già iscritti
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-center md:justify-end">
            <button
              type="button"
              onClick={openPrompt}
              className="inline-flex items-center gap-2 px-7 py-4 bg-gold text-midnight text-sm font-black uppercase tracking-widest hover:bg-yellow-300 transition-colors rounded-sm shadow-lg"
              data-testid="notification-inline-cta"
            >
              <Bell size={16} strokeWidth={3} />
              Attiva le notifiche
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotificationInlineCard;
